// @flow
/* global document */
import * as React from 'react';

import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { selectHelpdeskUrl } from '@gandi/authentication';
import { deleteCart, resetCart } from '@gandi/cart';
import { notifySuccess, notifyDanger } from '@gandi/notifications';
import { useTranslator } from '@gandi/translate';
import { Base } from '@gandi/ui';

import { useAppSelector } from 'src/hooks/useAppSelector';

import styles from './Conflict.scss';

function Conflict(): React.Node {
  const { __ } = useTranslator();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const supportURL = useAppSelector(selectHelpdeskUrl);

  const [deleting, setDeleting] = React.useState(false);

  React.useEffect(() => {
    // When using a hook conditionnally, place the condition *inside*
    // the hook as hooks must always be called in the same order.
    // (eslint react-hooks/rules-of-hooks)
    let el;
    let handleDeleteClick;
    if (!global.__SERVER__) {
      handleDeleteClick = async (evt: any) => {
        evt.preventDefault();
        if (deleting) {
          return;
        }

        setDeleting(true);
        try {
          await dispatch(deleteCart());
          dispatch(resetCart());
          dispatch(
            notifySuccess({
              message: __('Order cancelled successfully.'),
            }),
          );
          navigate('/cart/overview');
        } catch (err) {
          dispatch(
            notifyDanger({
              message: err._error || __('Unable to cancel this order.'),
            }),
          );
          setDeleting(false);
        }
      };
      el = document.getElementById('delete-cart');
      if (el) {
        el.addEventListener('click', handleDeleteClick);
      }
    }
    return () =>
      el ? el.removeEventListener('click', handleDeleteClick) : undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Base padding="small" backgroundColor="surface" separatorTop>
      <div
        className={styles.markdownContent}
        dangerouslySetInnerHTML={__(
          'This shopping cart cannot be billed to any of your organizations.' +
            '\n\n' +
            'This is likely because it contains one or more domain names ' +
            'that are managed by a reseller organization to which ' +
            'you do not have sufficient permissions.' +
            '\n\n' +
            'If you feel that this message is in error, ' +
            'please [contact customer support](%(supportURL)s).',
          {
            withMarkdown: true,
            supportURL,
          },
        )}
      />
      <div
        className={styles.markdownContent}
        dangerouslySetInnerHTML={__(
          'Alternatively, you can ' +
            '<a href="#" role="button" id="delete-cart">delete this shopping cart</a>.',
          { withHTML: true },
        )}
      />
    </Base>
  );
}

export default Conflict;
